import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import Cropper, { CropperImage } from 'cropperjs';

interface CropperTransformEvent extends CustomEvent {
  detail: {
    matrix: number[];
    oldMatrix: number[];
  };
}

@Component({
  selector: 'ark-lib-image-crop',
  templateUrl: './image-crop.component.html',
  styleUrl: './image-crop.component.css',
})
export class ImageCropComponent implements AfterViewInit, OnDestroy {
  @ViewChild('container', { static: true })
  container!: ElementRef<HTMLElement>;

  imageFile = input<File | undefined>(undefined);

  cropper!: Cropper;
  private cropperImage?: CropperImage;

  private readonly preventNonFullBleedTransform = (event: Event) => {
    const transformEvent = event as CropperTransformEvent;
    const selection = this.cropper.getCropperSelection();
    const cropperImage = this.cropperImage;

    if (!selection || !cropperImage || !transformEvent.detail) {
      return;
    }

    const { matrix, oldMatrix } = transformEvent.detail;
    const currentImageRect = cropperImage.getBoundingClientRect();
    const selectionRect = selection.getBoundingClientRect();
    const oldScaleX = Math.hypot(oldMatrix[0], oldMatrix[1]);
    const oldScaleY = Math.hypot(oldMatrix[2], oldMatrix[3]);
    const newScaleX = Math.hypot(matrix[0], matrix[1]);
    const newScaleY = Math.hypot(matrix[2], matrix[3]);

    if (oldScaleX === 0 || oldScaleY === 0) {
      event.preventDefault();
      return;
    }

    const nextWidth = currentImageRect.width * (newScaleX / oldScaleX);
    const nextHeight = currentImageRect.height * (newScaleY / oldScaleY);
    const nextCenterX =
      currentImageRect.left +
      currentImageRect.width / 2 +
      (matrix[4] - oldMatrix[4]);
    const nextCenterY =
      currentImageRect.top +
      currentImageRect.height / 2 +
      (matrix[5] - oldMatrix[5]);
    const tolerance = 0.5;
    const coversSelection =
      nextCenterX - nextWidth / 2 <= selectionRect.left + tolerance &&
      nextCenterY - nextHeight / 2 <= selectionRect.top + tolerance &&
      nextCenterX + nextWidth / 2 >= selectionRect.right - tolerance &&
      nextCenterY + nextHeight / 2 >= selectionRect.bottom - tolerance;

    if (!coversSelection) {
      event.preventDefault();
    }
  };

  croppedImage = new EventEmitter<any>();

  async ngAfterViewInit() {
    this.create(this.imageFile());
  }

  ngOnDestroy() {
    this.cropperImage?.removeEventListener(
      'transform',
      this.preventNonFullBleedTransform,
    );
    this.cropper?.destroy();
  }

  create(file: File | undefined) {
    if (!file) return;

    this.readFile(file, (url) => {
      const image = new Image();
      image.alt = 'Picture';
      image.height = 200;
      image.src = url;

      this.createCropper(image);
    });
  }
  createCropper(image: HTMLImageElement) {
    this.cropper = new Cropper(image, {
      container: this.container.nativeElement,
      template: `
      <cropper-canvas
        background
        data-zoom-ignore="true"
        style="aspect-ratio: 9 / 16; width: min(33.75vh, 360px, 100%); width: min(33.75dvh, 360px, 100%);"
      >
        <cropper-image initial-center-size="cover" scalable translatable></cropper-image>
        <cropper-shade hidden></cropper-shade>
        <cropper-selection
          initial-aspect-ratio="0.5625"
          initial-coverage="1"
          outlined
        >
          <cropper-grid role="grid" bordered covered></cropper-grid>
          <cropper-crosshair centered></cropper-crosshair>
        </cropper-selection>
        <cropper-handle action="move" plain></cropper-handle>
      </cropper-canvas>
      `,
    });

    this.cropperImage = this.cropper.getCropperImage() ?? undefined;
    void this.cropperImage?.$ready(() => {
      this.cropperImage?.addEventListener(
        'transform',
        this.preventNonFullBleedTransform,
      );
    });
  }

  readFile(file: File, output: (url: string) => void) {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target) {
        output(e.target.result as string);
      }
    };
    reader.readAsDataURL(file);
  }


  async getBlob(): Promise<Blob> {
    return new Promise(async (resolve, reject) => {
      const canvas = await this.cropper.getCropperSelection()?.$toCanvas({
        width: 540,
        height: 960,
      });
      if (canvas) {
        const png = canvas.toBlob((blob) => {
          resolve(blob as Blob);
        }, 'image/png');
      } else {
        reject();
      }
    });
  }

  async getFile(): Promise<File> {
    const blob = await this.getBlob();
    const file = new File([blob], 'image.png', {
      type: blob.type,
    });
    return file;
  }
}
